import EmberObject, { computed } from '@ember/object';
import { A } from '@ember/array';
import Service, { inject as service } from '@ember/service';
import { initializePropertyDefaults } from 'sh-www/utils/initialize-state-properties';
import { v1 } from "ember-uuid";
import { APPLICATION } from "../../../models/application";
import {
  POSTING_APPLICATIONS_SORT_API_ENUMS
} from "../../../constants/routes/posting";
import { ICON_SORTS } from "../../../constants/lsg";

export default Service.extend({
  /**
   * Register services
   */
  errorTracker: service('error-tracker'),

  /**
   * Setup default state here
   */
  propertyDefaults: {
    /**
     * Assignment loaded from server for the detailed view
     *
     * @type {Assignment|null}
     */
    assignment: null,

    /**
     * The current view's status for which application list tab we're showing.  e.g. pending, hired, declined.
     *
     * @type {String}   of type APPLICATION.SIMPLE_STATUS.*
     */
    currentApplicationFilterStatus: APPLICATION.SIMPLE_STATUS.PENDING,

    /**
     * Hold the current search results in this array.
     *
     * @type {Ember.MutableArray}
     */
    applicationSearchResults: () => A(),

    /**
     * The model representing the list of applications we'll show on the assignment details view.
     *
     * @type {AssignmentDetailsApplicationList|null}
     */
    assignmentDetailsApplicationList: null,

    /**
     * We'll use this to store the results from the primary assignmentDetailsApplicationList while we're searching.
     * When search is (de)activated, we will swap this cache with the primary list which will allow all
     * the downstream state to be set up correctly.
     *
     * @type {AssignmentDetailsApplicationList|null}
     */
    assignmentDetailsApplicationListWhileSearchingCache: null,

    /**
     * When this cache key changes, we'll know our current client side cache has become invalid and
     * should be reset.
     *
     * This should be random key that we'll generate for each set of search results.  This should stay
     * the same during paging but should change any other time.
     *
     * @type {String}   The current cache key of the postings list.
     */
    currentCacheKey: () => v1(),

    /**
     * When this cache key changes, we'll know our current client side cache has become invalid and
     * should be reset.
     *
     * This should be random key that we'll generate for each set of search results.  This should stay
     * the same during paging but should change any other time.
     *
     * @type {String}   The current cache key of the postings list that was retrieved by the search API call.
     */
    currentCacheKeySearch: () => v1(),

    /**
     * The current number of outstanding requests for pages that are in flight.
     *
     * @type {Number}
     */
    currentOutstandingPagesCount: 0,

    /**
     * The current pagination model
     *
     * @type {Pagination|null}
     */
    currentPagination: null,

    /**
     * Set to true when filter favorites.
     *
     * @type {Boolean}
     */
    isFilterFavorites: false,

    /**
     * This will be true while we're loading all of the pages for the user.
     *
     * @type {Boolean}
     */
    isLoadingAllPages: false,

    /**
     * True when we're loading an assignment from the server.
     *
     * @type {Boolean}
     */
    isLoadingAssignment: false,

    /**
     * True when we're loading search results from the server.
     *
     * @type {Boolean}
     */
    isLoadingSearchResult: false,

    /**
     * True when the search component is activated
     *
     * @type {Boolean}
     */
    isSearchActivated: false,

    /**
     * True if the user has the select all option checked
     *
     * @type {Boolean}
     */
    isSelectAllChecked: false,

    /**
     * Will be set to true when we want to show the 'multi-select' options in the list view.
     *
     * @type {Boolean}
     */
    isShowSelectAllOptions: false,

    /**
     * A map of the page number => Application Objects.  When the cache key changes,
     * this cache will be reset.
     *
     * @type {Object}
     */
    pageToResultsMap: () => EmberObject.create({}),

    /**
     * current secondary sort
     *
     * @type {String} of type ICON_SORTS.SORT_DESCENDING
     */
    secondarySort: ICON_SORTS.SORT_DESCENDING,

    /**
     * current sort.
     *
     * @type {String} of type POSTING_APPLICATIONS_SORT_API_ENUMS.*
     */
    sortSelectedValue: POSTING_APPLICATIONS_SORT_API_ENUMS.USER_SEARCH_RANK,

    /**
     * List of the application ids selected in the multi-select application view.
     *
     * @type {Array<ApplicationId>}
     */
    totalApplicationsSelected: [],

    /**
     * Object of applicationId => Boolean of whether or not each application has been selected in
     * the list.
     *
     * @type {Object}
     */
    totalApplicationsSelectedMap: {},
  },

  /**
   * List of applications for the current view.
   *
   * @type {Ember.MutableArray<Application>}
   */
  applicationListItems: computed(
  'applicationSearchResults',
  'assignmentDetailsApplicationList.applications.[]',
  'isSearchActivated',
  function() {
    if (this.get('isSearchActivated')) {
      return this.get('applicationSearchResults');
    }

    let applications = A(),
      pagination = this.get('currentPagination'),
      resultMap = this.get('pageToResultsMap');

    if (!pagination) {
      //this can be empty if our state is reset while there are still pending xhr requests out
      //as in the case of pre-loading the applications.
      return A();
    }

    for (let i = 1; i <= pagination.get('totalPages'); i++) {
      if (resultMap[i]) {
        let appsToAdd = resultMap[i].get('applications');
        for (let j = 0; j < appsToAdd.get('length'); j++) {
          let appToAdd = appsToAdd.objectAt(j);
          if (applications.includes(appToAdd)) {
            this.get('errorTracker').error('applicationListItems - Duplicate application', this);
          } else {
            applications.addObject(appToAdd);
          }
        }
      }
    }

    return applications;
  }),

  /**
   * These are the previous counts for the navigation.  Essentially, we'll always have a previous count
   * so the navigation won't change as we click through the different menus.
   *
   * @type {Number}
   * @private
   */
  previousPendingCount: 0,
  previousHiredCount: 0,
  previousDeclinedCount: 0,

  /**
   * These are the 'pending' counts for the navigation.
   *
   * @type {Number}
   * @private
   */
  pendingCount: computed('assignmentDetailsApplicationList.pendingCount', function() {
    let previousCount = this.get('previousPendingCount'),
      newCount        = this.get('assignmentDetailsApplicationList.pendingCount');

    if (newCount != null) {
      this.set('previousPendingCount', newCount);
      return newCount;
    }

    return previousCount;
  }),

  /**
   * These are the 'hired' counts for the navigation.
   *
   * @type {Number}
   * @private
   */
  hiredCount: computed('assignmentDetailsApplicationList.hiredCount', function() {
    let previousCount = this.get('previousHiredCount'),
      newCount        = this.get('assignmentDetailsApplicationList.hiredCount');

    if (newCount != null) {
      this.set('previousHiredCount', newCount);
      return newCount;
    }

    return previousCount;
  }),

  /**
   * These are the 'declined' counts for the navigation.
   *
   * @type {Number}
   * @private
   */
  declinedCount: computed('assignmentDetailsApplicationList.declinedCount', function() {
    let previousCount = this.get('previousDeclinedCount'),
      newCount        = this.get('assignmentDetailsApplicationList.declinedCount');

    if (newCount != null) {
      this.set('previousDeclinedCount', newCount);
      return newCount;
    }

    return previousCount;
  }),

  /**
   * Keep track of the previous state to show this while we're loading b/t tabs.
   *
   * @type {Boolean}
   */
  previousPendingIsRead: false,
  previousHiredIsRead: false,
  previousDeclinedIsRead: false,

  /**
   * @type {Boolean}
   */
  pendingIsRead: computed('assignmentDetailsApplicationList.pendingIsRead', function() {
    return this._checkIsReadWithPrevious('pendingIsRead', 'previousPendingIsRead');
  }),

  /**
   * @type {Boolean}
   */
  hiredIsRead: computed('assignmentDetailsApplicationList.hiredIsRead', function() {
    return this._checkIsReadWithPrevious('hiredIsRead', 'previousHiredIsRead');
  }),

  /**
   * @type {Boolean}
   */
  declinedIsRead: computed('assignmentDetailsApplicationList.declinedIsRead', function() {
    return this._checkIsReadWithPrevious('declinedIsRead', 'previousDeclinedIsRead');
  }),

  /**
   * Given the prop and previousProp name check the relevant isRead status to send back.
   *
   * @param {String} propName
   * @param {String} previousPropName
   * @private
   */
  _checkIsReadWithPrevious(propName, previousPropName) {
    let isRead = this.get(`assignmentDetailsApplicationList.${propName}`);
    if (isRead == null) {
      return this.get(previousPropName);
    }

    this.set(previousPropName, isRead);
    return isRead;
  },

  /**
   * True if we're loading the first page of results only.  Useful for knowing we're not fetching pages in the background.
   *
   * @type {Boolean}
   */
  isLoadingFirstPage: computed('currentPagination', 'isLoadingResult', function() {
    return !this.get('currentPagination') && this.get('isLoadingResult');
  }),

  /**
   * @type {Boolean}    True when the first search on the page has an empty set.
   */
  isEmptyDefaultSearch: computed('isFirstQuery', 'applicationListItems.length', function() {
    return !this.get('applicationListItems.length') && this.get('isFirstQuery');
  }),

  /**
   * This is the first query run on a 'page' load.  e.g. a tab click from 'live' => 'draft' would need be a 'firstQuery',
   * but updating any sorts or filters would NOT be a `firstQuery`
   *
   * @type {Boolean}    True when the ONLY query that has been run is the first for the page.
   */
  isFirstQuery: true,

  /**
   * Will be set to true when our state is being reset in between transitions.  This is typically
   * useful for aborting synchronous observers as we reset their values.
   *
   * @type {Boolean}
   */
  isResettingState: false,

  /** @type {Assignment} assignment id for which to show buyer feedback */
  buyerFeedbackAssignment: null,

  initializeDefaults() {
    initializePropertyDefaults(this);
  },

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);
    this.initializeDefaults();
    this.set('isResettingState', false);
  }
});
